import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from 'react';
import { useProject } from './ProjectContext';
import { useProjectCompetitors } from '../hooks/useProjectCompetitors';
import {
  buildBrandHostRegistry,
  resolveHostFromRegistry,
  resolvePrimaryTrackingHost,
} from '../utils/brand-host-registry';

interface BrandLogoResolveOptions {
  relationship?: string | null;
  brandType?: string | null;
}

interface BrandLogoContextValue {
  ownBrandHost: string | null;
  resolveHost: (
    name?: string | null,
    host?: string | null,
    options?: BrandLogoResolveOptions,
  ) => string | null;
}

const BrandLogoContext = createContext<BrandLogoContextValue>({
  ownBrandHost: null,
  resolveHost: (_name, host) => (host ? host : null),
});

export function BrandLogoProvider({ children }: { children: ReactNode }) {
  const { projectId, project, data } = useProject();
  const competitors = useProjectCompetitors(projectId);

  const ownBrandHost = useMemo(() => {
    const fromOwn = data?.visibilitySummary.ownBrand?.domain;
    if (fromOwn) return fromOwn;
    return resolvePrimaryTrackingHost(data?.insight.domains);
  }, [data]);

  const registry = useMemo(() => {
    const ownNames = [
      project?.domain,
      data?.visibilitySummary.ownBrand?.canonicalName,
      ...(data?.insight.brandAliases ?? []).map((alias) => alias.name),
      ...(data?.visibilitySummary.topBrands ?? [])
        .filter((brand) => brand.relationship === 'you')
        .map((brand) => brand.canonicalName),
    ];

    return buildBrandHostRegistry({
      domains: data?.insight.domains,
      brandAliases: data?.insight.brandAliases,
      competitors,
      ownBrandNames: ownNames,
      extraBrands: [
        ...(data?.visibilitySummary.topBrands ?? []).map((brand) => ({
          name: brand.canonicalName,
          host:
            brand.domain ??
            (brand.relationship === 'you' ? ownBrandHost : null),
        })),
        ...(data?.visibilitySummary.topCompetitors ?? []).map((brand) => ({
          name: brand.canonicalName,
          host: brand.domain,
        })),
        data?.visibilitySummary.ownBrand
          ? {
              name: data.visibilitySummary.ownBrand.canonicalName,
              host: data.visibilitySummary.ownBrand.domain ?? ownBrandHost,
            }
          : null,
      ].filter((entry): entry is { name: string; host: string | null } => Boolean(entry)),
    });
  }, [data, competitors, project?.domain, ownBrandHost]);

  const value = useMemo<BrandLogoContextValue>(
    () => ({
      ownBrandHost,
      resolveHost: (name, host, options) =>
        resolveHostFromRegistry(registry, name, host, {
          ...options,
          ownBrandHost,
        }),
    }),
    [registry, ownBrandHost],
  );

  return (
    <BrandLogoContext.Provider value={value}>{children}</BrandLogoContext.Provider>
  );
}

export function useBrandLogoHost(
  name?: string | null,
  host?: string | null,
  options?: BrandLogoResolveOptions,
): string | null {
  const { resolveHost } = useContext(BrandLogoContext);
  return resolveHost(name, host, options);
}
